import { Fragment, useState } from "react";
import { Link } from "react-router-dom";

import { Alert } from "../../../components/Alert/Alert";
import { Input } from "../../../components/Input/Input";
import { Select } from "../../../components/Select/Select";
import { Switch } from "../../../components/Switch/Switch";
import { FieldRow } from "../../projects/config/FieldRow";
import { SectionCard } from "../../projects/config/SectionCard";
import {
  checkPayload,
  payloadProblemText,
  requiredKeys,
} from "../../projects/config/payloadSchema";
import type { HelpRequestType } from "../../projects/config/types";
import type { MetaSchemaField } from "../../../components/MetaSchemaEditor/MetaSchemaEditor";
import { defaultHelpKind } from "../config/defaults";
import type {
  BotsHelpConfig,
  BotsHelpKind,
  BotsHelpPayload,
} from "../config/types";
import { helpTypesLink } from "../links";
import styles from "./BotsSettings.module.css";

interface Props {
  value: BotsHelpConfig;
  onChange: (patch: Partial<BotsHelpConfig>) => void;
  dirty: boolean;
  /**
   * `help_request.types` of this project — what a help-kind row may name, and
   * what its payload templates are checked against. The whole rows rather than
   * their names, because a type now carries a `payload_schema` and the
   * templates live here while the schema lives on the Config page.
   */
  helpTypes: HelpRequestType[];
  /** For the way across to the Config page, where a schema is edited. */
  projectID: string;
}

/**
 * `bots.help` — the one layer that never runs on probability, the weights bots
 * use between themselves, and what they ask FOR.
 *
 * Mockup: bots-page-shell.html §4, "Help & guaranteed response", extended by
 * .codelane/artifacts/t-124/bots-help-payloads.html L354-L455 (the payloads
 * sub-line) and L482-L537 (a template the schema will not take).
 */
export function HelpSection({
  value,
  onChange,
  dirty,
  helpTypes,
  projectID,
}: Props) {
  return (
    <SectionCard
      id="help"
      title="Help & guaranteed response"
      apiName="bots.help"
      dirty={dirty}
    >
      <FieldRow
        name="Answer real requests"
        apiName="bots.help.guaranteed_response"
        hint="A real player's help request is always answered, even in a guild that is otherwise idle. This is the one thing that never runs on probability."
      >
        <Switch
          checked={value.guaranteed_response}
          onChange={(next) => onChange({ guaranteed_response: next })}
          label={value.guaranteed_response ? "Always" : "By probability"}
        />
        <div className={styles.helper}>
          Bots between themselves still use the weights below.
        </div>
      </FieldRow>

      <FieldRow
        name="Help kinds"
        apiName="bots.help.kinds[]"
        hint={
          <>
            One row per <code>help_request.types</code> entry of this project.
            Weight decides how often bots ask for it themselves; delays decide
            how fast they answer. <b>Payloads</b> decide what they ask{" "}
            <i>for</i> — a type whose schema declares a required field cannot be
            requested without one.
          </>
        }
        // Seven columns with an 816px minimum against the 606px a two-column
        // row leaves once the section rail has taken its 258px — the widest
        // editor on the page, and the one that scrolled worst after the move.
        stacked
      >
        <HelpKindsEditor
          rows={value.kinds}
          helpTypes={helpTypes}
          projectID={projectID}
          onChange={(kinds) => onChange({ kinds })}
        />
        {value.kinds.length === 0 && (
          <div className={styles.helper}>
            No rows: every request type falls back to the platform defaults —
            180-minute interval, five helpers, first answer in 30–120 s. How
            long the request itself lives is the type's own <code>ttl</code>, up
            in Help requests on the Config page. Layer 4 works without a single
            row here.
          </div>
        )}
        {helpTypes.length === 0 && (
          <Alert variant="warn" title="This project declares no help types">
            <code>help_request.types</code> is empty, so bots have nothing to
            ask for. They still answer whatever the game creates.
          </Alert>
        )}
      </FieldRow>
    </SectionCard>
  );
}

function HelpKindsEditor({
  rows,
  helpTypes,
  projectID,
  onChange,
}: {
  rows: BotsHelpKind[];
  helpTypes: HelpRequestType[];
  projectID: string;
  onChange: (next: BotsHelpKind[]) => void;
}) {
  const patch = (i: number, p: Partial<BotsHelpKind>) =>
    onChange(rows.map((r, j) => (i === j ? { ...r, ...p } : r)));

  /**
   * Write a kind's templates. An emptied list DELETES the key rather than
   * storing `[]`: the two say the same thing to the simulation — the bot asks
   * with no payload — and absent is what a kind that never had one already
   * says, so removing the last template leaves the config exactly as it was
   * before the first was added. Same rule as a request type's payload schema.
   */
  const patchPayloads = (i: number, next: BotsHelpPayload[]) =>
    onChange(
      rows.map((r, j) => {
        if (i !== j) return r;
        const out = { ...r };
        if (next.length === 0) delete out.payloads;
        else out.payloads = next;
        return out;
      }),
    );

  const names = helpTypes.map((t) => t.type);
  const unused = names.filter((t) => !rows.some((r) => r.request_type === t));
  const schemaOf = (requestType: string) =>
    helpTypes.find((t) => t.type === requestType)?.payload_schema;

  return (
    <>
      <div className={styles.gridEditor}>
        <div className={styles.scroller}>
          <div className={`${styles.row} ${styles.kinds} ${styles.head}`}>
            <div>request_type</div>
            <div className={styles.num}>weight</div>
            <div className={styles.num}>interval, min</div>
            <div className={styles.num}>helpers</div>
            <div className={styles.num}>first delay, s</div>
            <div className={styles.num}>next delay, s</div>
            <div />
          </div>
          {rows.map((k, i) => (
            <div
              key={i}
              className={`${styles.row} ${styles.kinds} ${styles.tall}`}
            >
              <div className={styles.cell}>
                <Select
                  className={styles.rowSelect}
                  value={k.request_type}
                  onChange={(v) => patch(i, { request_type: v })}
                  options={[
                    ...(names.includes(k.request_type) || !k.request_type
                      ? []
                      : [
                          {
                            value: k.request_type,
                            label: `${k.request_type} (removed)`,
                          },
                        ]),
                    ...names.map((t) => ({
                      value: t,
                      label: t,
                      mono: true,
                    })),
                  ]}
                  placeholder="request type"
                />
              </div>
              <Input
                type="number"
                value={String(k.weight)}
                onChange={(e) => patch(i, { weight: Number(e.target.value) })}
                aria-label={`${k.request_type} weight`}
              />
              <Input
                type="number"
                value={String(k.interval_minutes)}
                onChange={(e) =>
                  patch(i, {
                    interval_minutes: Number(e.target.value),
                  })
                }
                aria-label={`${k.request_type} interval`}
              />
              <Input
                type="number"
                value={String(k.max_helpers)}
                onChange={(e) =>
                  patch(i, {
                    max_helpers: Number(e.target.value),
                  })
                }
                aria-label={`${k.request_type} helpers`}
              />
              <div className={styles.pair}>
                <Input
                  type="number"
                  value={String(k.first_delay_min_seconds)}
                  onChange={(e) =>
                    patch(i, {
                      first_delay_min_seconds: Number(e.target.value),
                    })
                  }
                  aria-label={`${k.request_type} first delay minimum`}
                />
                <span className={styles.dash}>—</span>
                <Input
                  type="number"
                  value={String(k.first_delay_max_seconds)}
                  onChange={(e) =>
                    patch(i, {
                      first_delay_max_seconds: Number(e.target.value),
                    })
                  }
                  aria-label={`${k.request_type} first delay maximum`}
                />
              </div>
              <div className={styles.pair}>
                <Input
                  type="number"
                  value={String(k.next_delay_min_seconds)}
                  onChange={(e) =>
                    patch(i, {
                      next_delay_min_seconds: Number(e.target.value),
                    })
                  }
                  aria-label={`${k.request_type} next delay minimum`}
                />
                <span className={styles.dash}>—</span>
                <Input
                  type="number"
                  value={String(k.next_delay_max_seconds)}
                  onChange={(e) =>
                    patch(i, {
                      next_delay_max_seconds: Number(e.target.value),
                    })
                  }
                  aria-label={`${k.request_type} next delay maximum`}
                />
              </div>
              <button
                type="button"
                className={styles.rowBtn}
                aria-label={`Remove ${k.request_type || `kind ${i + 1}`}`}
                onClick={() => onChange(rows.filter((_, j) => j !== i))}
              >
                ✕
              </button>
              <PayloadsSubRow
                requestType={k.request_type}
                schema={schemaOf(k.request_type)}
                value={k.payloads ?? []}
                projectID={projectID}
                onChange={(payloads) => patchPayloads(i, payloads)}
              />
            </div>
          ))}
        </div>
        <button
          type="button"
          className={styles.addRow}
          disabled={unused.length === 0}
          onClick={() => onChange([...rows, defaultHelpKind(unused[0] ?? "")])}
        >
          {unused.length === 0
            ? "+ Add kind — every declared type is already listed"
            : "+ Add kind"}
        </button>
      </div>
      <div className={styles.helper}>
        Share is weight ÷ the row's total — what the bot actually picks with.
        One payload and no weights is the simple case: it is chosen every time.
      </div>
    </>
  );
}

/**
 * The payload templates of one help kind: what a bot of this kind asks for.
 *
 * The type's schema is READ here and edited on the Config page — two documents,
 * and the `schema:` line is what makes the one this page cannot change visible
 * beside the templates it governs.
 */
function PayloadsSubRow({
  requestType,
  schema,
  value,
  projectID,
  onChange,
}: {
  requestType: string;
  schema: MetaSchemaField[] | undefined;
  value: BotsHelpPayload[];
  projectID: string;
  onChange: (next: BotsHelpPayload[]) => void;
}) {
  /**
   * What is actually in each payload box while it is being typed, by row.
   *
   * The row holds a VALUE, so a box rendered from it would be rewritten under
   * the caret the moment the text parses — the same problem, and the same cure,
   * as the donor reward box on the Config page.
   */
  const [drafts, setDrafts] = useState<Record<number, string>>({});

  const total = value.reduce((n, p) => n + (p.weight > 0 ? p.weight : 0), 0);

  const patch = (i: number, next: Partial<BotsHelpPayload>) =>
    onChange(value.map((p, j) => (i === j ? { ...p, ...next } : p)));

  /** Removing the last template DELETES the key rather than storing `[]`: the
   *  two say the same thing to the simulation, and absent is what a kind that
   *  never had one already says. Same rule as a request type's schema. */
  const remove = (i: number) => onChange(value.filter((_, n) => n !== i));

  /**
   * Write a template from what the operator typed. Text that IS a payload is
   * stored parsed — it is a value in the config and goes on the wire as one;
   * text that is not one yet is stored AS TEXT, which paints the box red and
   * blocks Save until it becomes a payload or goes away. Nothing else can put a
   * string in this field, so nothing else is mistaken for one.
   */
  const patchPayload = (i: number, raw: string) => {
    setDrafts((prev) => ({ ...prev, [i]: raw }));
    let parsed: unknown = raw;
    try {
      const v: unknown = JSON.parse(raw);
      // An array or a scalar is not a payload either, and storing one would
      // lose the operator's own text on the way to the same refusal.
      if (v && typeof v === "object" && !Array.isArray(v)) parsed = v;
    } catch {
      // Left as the text they typed.
    }
    patch(i, { payload: parsed });
  };

  const textOf = (i: number, payload: unknown): string => {
    const draft = drafts[i];
    if (draft !== undefined && sameJSON(draft, payload)) return draft;
    if (typeof payload === "string") return payload;
    return JSON.stringify(payload, null, 1)
      .replace(/,\n\s*/g, ", ")
      .replace(/\n\s*/g, "");
  };

  const required = requiredKeys(schema);
  const needsAPayload = required.length > 0;
  // "Bots will never ask for this" is about whether a template can be DRAWN:
  // an empty list and a list whose every weight is zero are the same silence.
  const cannotAsk = needsAPayload && total === 0;

  return (
    <div className={styles.plSub}>
      <div className={styles.plLabel}>payloads</div>
      <div>
        <div className={styles.plList}>
          {value.length > 0 && (
            <div className={styles.plHead}>
              <div>payload</div>
              <div className={styles.plRight}>weight</div>
              <div className={styles.plRight}>share</div>
              <div />
            </div>
          )}
          {value.length === 0 && (
            <div className={styles.plNone}>
              {needsAPayload
                ? "No payloads."
                : "No payloads — bots create this request with no payload, exactly as they do today."}
            </div>
          )}
          {value.map((p, j) => {
            const problems =
              typeof p.payload === "string"
                ? [{ reason: "not_an_object" as const }]
                : checkPayload(p.payload, schema);
            const bad = problems.length > 0;
            return (
              // A Fragment, not a wrapper: the row and its messages are
              // children of `.plList` and take its 6px gap, which a div of
              // their own would swallow (mockup L487-L503).
              <Fragment key={j}>
                <div className={styles.plRow}>
                  <Input
                    className={`${styles.mono} ${bad ? styles.err : ""}`}
                    value={textOf(j, p.payload)}
                    onChange={(e) => patchPayload(j, e.target.value)}
                    placeholder='{"card_id": "ace_fire"}'
                    aria-label={`${requestType} payload ${j + 1}`}
                    aria-invalid={bad || undefined}
                  />
                  <Input
                    type="number"
                    value={String(p.weight)}
                    onChange={(e) =>
                      patch(j, {
                        weight: Number(e.target.value),
                      })
                    }
                    aria-label={`${requestType} payload ${j + 1} weight`}
                  />
                  <div className={styles.share}>
                    {bad || total <= 0 || p.weight <= 0
                      ? "—"
                      : `${Math.round((p.weight / total) * 100)}%`}
                  </div>
                  <button
                    type="button"
                    className={styles.plDel}
                    aria-label={`Remove ${requestType} payload ${j + 1}`}
                    onClick={() => remove(j)}
                  >
                    ✕
                  </button>
                </div>
                {problems.map((problem, n) => (
                  <div key={n} className={styles.plErr}>
                    {payloadProblemText(problem, schema)}
                  </div>
                ))}
              </Fragment>
            );
          })}
          <button
            type="button"
            className={styles.plAdd}
            onClick={() => onChange([...value, { weight: 1, payload: {} }])}
          >
            + Add payload
          </button>
        </div>
        <div className={styles.plSchema}>{schemaLine(schema)}</div>
        {cannotAsk && (
          <Alert
            variant="warn"
            title={
              <>
                Bots will never ask for <code>{requestType}</code>
              </>
            }
          >
            Its schema requires <code>{required.join(", ")}</code>,
            and there is no payload here to put one in. Bots still <b>answer</b>{" "}
            requests of this type that the game creates.{" "}
            <Link to={helpTypesLink(projectID)}>Open the schema →</Link>
          </Alert>
        )}
      </div>
    </div>
  );
}

/**
 * The `schema:` line — what a template of this kind owes, read out of
 * `help_request.types[].payload_schema` and not editable here.
 *
 * The allow-list is named after the fields, as the mockup writes it. When more
 * than one field carries one the key goes in front of it, which is the only
 * thing the mockup leaves unsaid — with one list its own sentence is
 * unambiguous, with two it would not be.
 */
function schemaLine(schema: MetaSchemaField[] | undefined) {
  if (!schema || schema.length === 0) {
    return (
      <>
        schema: <b>none</b> — this type accepts any payload
      </>
    );
  }
  const withLists = schema.filter((f) => (f.allowed ?? []).length > 0);
  return (
    <>
      schema:{" "}
      {schema.map((f, i) => (
        <span key={f.key}>
          {i > 0 && " · "}
          <b>
            {f.key}
            {f.required && "*"}
          </b>{" "}
          {f.type}
        </span>
      ))}
      {withLists.map((f) => (
        <span key={f.key}>
          {"  ·  "}
          {withLists.length > 1 ? `${f.key} allowed: ` : "allowed: "}
          {(f.allowed ?? []).join(", ")}
        </span>
      ))}
    </>
  );
}

/** Whether the text in a payload box still MEANS what its row holds — the same
 *  test, and the same reason, as the donor reward box's `sameReward`. */
function sameJSON(text: string, payload: unknown): boolean {
  try {
    const v: unknown = JSON.parse(text);
    if (!v || typeof v !== "object" || Array.isArray(v))
      return text === payload;
    return JSON.stringify(v) === JSON.stringify(payload);
  } catch {
    return text === payload;
  }
}
